import React, { useState } from 'react';
import { Lock } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import StepIndicator from '../../components/authComp/signUp/StepIndicator';
import AddressForm from '../../components/authComp/signUp/AddressForm';
import AccountForm from '../../components/authComp/signUp/AccountForm';
import { useTranslation } from 'react-i18next';

function Signup() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    country: '',
    district: '',
    city: '',
    parish: '',
    postalCode: '',
    street: '',
    doorNumber: '',
    apartment: '',
    nif: '',
    password: '',
    confirmPassword: '',
  });

  const [passwordChecks, setPasswordChecks] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    special: false
  });

  const validatePassword = (password) => {
    const regex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&.,])[A-Za-z\d@$!%*?&.,]{8,}$/;
    return regex.test(password);
  };


  const handleCountryChange = (selectedOption) => {
    setFormData(prev => ({
      ...prev,
      country: selectedOption.value,
    }));
  };

  const validateFirstStep = () => {
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.password || !formData.confirmPassword) {
      setError('Please fill in all required fields');
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    if (!validatePassword(formData.password)) {
      setError('Password does not meet requirements');
      return false;
    }
    return true;
  };

  const validateSecondStep = () => {
    if (
      !formData.phone ||
      !formData.country ||
      !formData.district ||
      !formData.city ||
      !formData.parish ||
      !formData.postalCode ||
      !formData.street ||
      !formData.doorNumber ||
      !formData.apartment ||
      !formData.nif
    ) {
      setError('Please fill in all required fields');
      return false;
    }
    return true;
  };

  const handleNextStep = () => {
    if (validateFirstStep()) {
      setError('');
      setStep(2);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (name === 'password') {
      setPasswordChecks({
        length: value.length >= 8,
        uppercase: /[A-Z]/.test(value),
        lowercase: /[a-z]/.test(value),
        number: /[0-9]/.test(value),
        special: /[@$!%*?&#.]/.test(value)
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateSecondStep()) {
      return;
    }

    setLoading(true);
    try {
      const response = await fetch('https://www.ia-schedule.com:4139/api/auth/signup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Registration failed');
      }

      sessionStorage.setItem('signupSuccess', 'Your account has been created successfully.')
      navigate('/login')
    } catch (err) {
      setError(err.message || 'An error occurred during registration');
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen flex flex-col items-center py-16 -mt-16 bg-gray-100 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl w-full">
        <div className="text-center">
          <Lock className="mx-auto h-12 w-12 text-indigo-600" />
          <h2 className="mt-4 text-3xl font-extrabold text-gray-900">
            {t("auth.signup.title")}
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            {t("auth.signup.step")} {step} {t("auth.signup.of")} 2: {step === 1 ? t("auth.signup.accountInfo") : t("auth.signup.personalDetails")}
          </p>
        </div>

        {error && (
          <div className="mt-4 p-3 bg-red-50 text-red-500 rounded-lg text-sm">
            {error}
          </div>
        )}

        <div className="mt-8 bg-white shadow rounded-lg p-8">
          <StepIndicator currentStep={step} translations={t} />
          <form onSubmit={handleSubmit}>
            {step === 1 ? (
              <AccountForm
                formData={formData}
                handleChange={handleChange}
                passwordChecks={passwordChecks}
                showPassword={showPassword}
                setShowPassword={setShowPassword}
                handleNextStep={handleNextStep}
                translations={t}
              />
            ) : (
              <AddressForm
                formData={formData}
                loading={loading}
                handleChange={handleChange}
                handleCountryChange={handleCountryChange}
                setFormData={setFormData}
                setStep={setStep}
                onSubmit={handleSubmit}
                translations={t}
              />
            )}
          </form>
        </div>

        <div className='mt-8 text-center'>
          <span className='text-gray-600'>{t("auth.signup.alreadyHaveAccount")}</span>{' '}
          <Link to='/login' className='text-indigo-600 hover:text-indigo-500 font-medium'>
            {t("auth.signup.loginHere")}
          </Link>
        </div>
      </div>
    </main>
  );
}

export default Signup;